import { Injectable, LOCALE_ID, inject } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable } from 'rxjs';
import { languageFor } from '../common/formatting-locale';

/**
 * Sends the language of the page with every request (backend #682).
 *
 * The header wins over the user's stored setting in the backend's resolution chain, so this is
 * what decides the language of everything the server resolves for a request: category names, a
 * validation message it renders itself, and — since the mail slices — the wording and the
 * *snapshot* on an order placed in this session.
 *
 * It reads `LOCALE_ID`, i.e. the bundle the user is actually looking at, and not the stored
 * preference it used to read. Those two are different things and did disagree: `localStorage`
 * survives a visit that reached a locale without the menu (a bookmark on `/de/`, nginx routing
 * `/` by `Accept-Language`), so a user on `/en/` could ask the backend for German and get an
 * English page with German category names. The preference is a preference — which bundle to
 * serve this user next (slice B) — never a description of the page already on screen.
 *
 * `LOCALE_ID` is a formatting locale (`en-GB`), and the platform's language tags are bare, hence
 * `languageFor`. The backend folds region variants itself, so this is tidiness rather than a
 * requirement.
 */
@Injectable()
export class LanguageInterceptor implements HttpInterceptor {
  private readonly language: string;

  constructor() {
    const localeId = inject(LOCALE_ID);

    this.language = languageFor(localeId);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(
      req.clone({ headers: req.headers.set('Accept-Language', this.language) }),
    );
  }
}
